import { createContext, useContext, useMemo, type ReactNode } from "react";
import { localSupabaseClient, type TypedSupabaseClient } from "../../shared/api/supabase";
import { PublicAppError } from "../../shared/lib/errors";
import { createArtifactsApi, type ArtifactsApi } from "../artifacts/api/artifacts-api";
import { createAssistantApi, type AssistantApi } from "./api/assistant-api";

export interface AssistantApis {
  assistant: AssistantApi;
  artifacts: ArtifactsApi;
}

const AssistantApiContext = createContext<AssistantApis | null>(null);

export function AssistantApiProvider({
  children,
  client = localSupabaseClient,
  apis,
}: {
  children: ReactNode;
  client?: TypedSupabaseClient | null;
  apis?: AssistantApis;
}) {
  const value = useMemo(() => {
    if (apis !== undefined) return apis;
    if (client === null) return null;
    return { assistant: createAssistantApi(client), artifacts: createArtifactsApi(client) };
  }, [apis, client]);

  if (value === null) {
    throw new PublicAppError(
      "missing_public_config",
      "A configuração pública do assistente não está disponível.",
    );
  }
  return <AssistantApiContext.Provider value={value}>{children}</AssistantApiContext.Provider>;
}

export function useAssistantApis(): AssistantApis {
  const value = useContext(AssistantApiContext);
  if (value === null) throw new Error("AssistantApiProvider ausente");
  return value;
}
